import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Season start date — only count paid commissions after this date for "Total Paid Out"
const PAYOUT_SEASON_START = '2026-07-17';

const AC_COMPLETION_STAGES = [
  'Job Complete',
  'Pending Payment',
  'Paid and Closed',
  'Commission Paid',
  'Closed',
];

interface Commission {
  id: string;
  amount: number;
  status: string;
  type: string;
  created_at: string;
  paid_at: string | null;
  jobs: { id: string; leads: { address: string; homeowner_name: string; claim_number: string; }; job_type?: string; pipeline_stage_name?: string; } | null;
  users: { name: string; };
}

export default function CommissionsPage() {
  const [all, setAll] = useState<Commission[]>([]);
  const [pending, setPending] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [tab, setTab] = useState<'pending' | 'approved' | 'history'>('pending');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [approving, setApproving] = useState<string | null>(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        api.get('/commissions/all'),
        api.get('/commissions/pending'),
      ]);
      setAll(allRes.data);
      setPending(pendingRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const isApprovable = (c: Commission) => {
    if (c.type !== 'estimator') return true; // roofing always approvable
    return AC_COMPLETION_STAGES.includes(c.jobs?.pipeline_stage_name || '');
  };

  const approve = async (id: string, type?: string, amount?: number) => {
    const msg = type === 'estimator'
      ? `Approve estimator commission of $${amount}? Salesman will be notified.`
      : `Approve this $${amount} roofing commission? This will also send the homeowner intro email.`;
    if (!window.confirm(msg)) return;
    setApproving(id);
    try {
      await api.post(`/commissions/${id}/approve`);
      fetchAll();
    } catch (e) {
      alert('Failed to approve commission');
    } finally { setApproving(null); }
  };

  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const markAsPaid = async (id: string, amount: number, salesman: string) => {
    if (!window.confirm(`Mark $${amount.toFixed(2)} commission for ${salesman} as paid via QuickBooks?`)) return;
    setMarkingPaid(id);
    try {
      await api.post(`/commissions/${id}/paid`, { payment_method: 'quickbooks' });
      fetchAll();
    } catch (e) {
      alert('Failed to mark as paid');
    } finally { setMarkingPaid(null); }
  };

  const totalPaid = all.filter(c => c.status === 'paid' && c.paid_at && c.paid_at >= PAYOUT_SEASON_START).reduce((sum, c) => sum + c.amount, 0);
  const totalApproved = all.filter(c => c.status === 'approved').reduce((sum, c) => sum + c.amount, 0);
  const totalPending = all.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
  const applyJobTypeFilter = (list: Commission[]) =>
    jobTypeFilter === 'all' ? list :
    jobTypeFilter === 'roofing' ? list.filter(c => c.type === 'roofing') :
    list.filter(c => c.jobs?.job_type === jobTypeFilter);

  const approved = all.filter(c => c.status === 'approved');
  const display = applyJobTypeFilter(
    tab === 'pending' ? pending : 
    tab === 'approved' ? approved : 
    all.filter(c => c.status === 'paid')
  );

  return (
    <div style={{ padding: 32 }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 24 }}>Commissions</h1>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Pending Approval', value: `$${totalPending.toFixed(0)}`, color: '#F59E0B' },
          { label: 'Approved (Unpaid)', value: `$${totalApproved.toFixed(0)}`, color: '#8B5CF6' },
          { label: 'Total Paid Out', value: `$${totalPaid.toFixed(0)}`, color: '#10B981' },
          { label: 'Total Commissions', value: all.length, color: '#4A7EB5' },
        ].map(card => (
          <div key={card.label} style={{ background: '#1A2B3C', borderRadius: 14, padding: 20, border: '1px solid #2D4259' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 4 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['pending', `Pending (${pending.length})`], ['approved', `Approved (${approved.length})`], ['history', 'History']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key as any)}
            style={{
              padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: tab === key ? '#4A7EB5' : '#1A2B3C',
              color: tab === key ? '#fff' : '#8FA8C0',
              border: `1px solid ${tab === key ? '#4A7EB5' : '#2D4259'}`,
            }}
          >{label}</button>
        ))}
      </div>

      {/* Job Type Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ color: '#8FA8C0', fontSize: 13, alignSelf: 'center', marginRight: 4 }}>Job Type:</span>
        {[['all', 'All'], ['roofing', '🏠 Roofing'], ['asphalt', '🛣️ Asphalt'], ['concrete', '🪨 Concrete']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setJobTypeFilter(key)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: jobTypeFilter === key ? '#4A7EB5' : '#1A2B3C',
              color: jobTypeFilter === key ? '#fff' : '#8FA8C0',
              border: `1px solid ${jobTypeFilter === key ? '#4A7EB5' : '#2D4259'}`,
            }}
          >{label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2D4259' }}>
              {['Salesman', 'Homeowner', 'Address', 'Source', 'Job Type', 'Claim #', 'Amount', 'Date', tab === 'history' ? 'Paid On' : 'Action'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {display.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #2D4259', cursor: c.jobs?.id ? 'pointer' : 'default' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                onClick={() => c.jobs?.id && navigate(`/pipeline/${c.jobs.id}`)}
              >
                <td style={{ padding: '14px 16px', fontWeight: 600, color: '#fff' }}>{c.users?.name}</td>
                <td style={{ padding: '14px 16px', color: '#8FA8C0' }}>{c.jobs?.leads?.homeowner_name}</td>
                <td style={{ padding: '14px 16px', color: '#8FA8C0', fontSize: 13 }}>{c.jobs?.leads?.address}</td>
                <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      background: c.type === 'estimator' ? '#F59E0B22' : '#4A7EB522',
                      color: c.type === 'estimator' ? '#F59E0B' : '#4A7EB5',
                      border: `1px solid ${c.type === 'estimator' ? '#F59E0B44' : '#4A7EB544'}`,
                      borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: 700,
                    }}>{c.type === 'estimator' ? 'Estimator' : 'Salesman'}</span>
                  </td>
                <td style={{ padding: '14px 16px', color: '#94a3b8', textTransform: 'capitalize' }}>{c.type === 'estimator' ? (c.jobs?.job_type || '—') : 'Roofing'}</td>
                <td style={{ padding: '14px 16px', color: '#4A7EB5', fontWeight: 600 }}>{c.jobs?.leads?.claim_number || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#10B981', fontWeight: 700, fontSize: 16 }}>${c.amount}</td>
                <td style={{ padding: '14px 16px', color: '#8FA8C0', fontSize: 13 }}>{new Date(c.created_at).toLocaleDateString()}</td>
                <td style={{ padding: '14px 16px' }}>
                  {tab === 'pending' ? (
                    <div style={{ position: 'relative', display: 'inline-block' }} title={!isApprovable(c) ? `Job must reach completion stage first. Currently: ${c.jobs?.pipeline_stage_name}` : ''}>
                      <button
                        onClick={(e) => { e.stopPropagation(); if (isApprovable(c)) approve(c.id, c.type, c.amount); }}
                        disabled={approving === c.id || !isApprovable(c)}
                        style={{
                          background: isApprovable(c) ? '#10B981' : '#2D4259',
                          color: isApprovable(c) ? '#fff' : '#4A6580',
                          padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                          opacity: approving === c.id ? 0.6 : 1,
                          cursor: isApprovable(c) ? 'pointer' : 'not-allowed',
                          border: 'none',
                        }}
                      >
                        {approving === c.id ? 'Approving...' : isApprovable(c) ? '✓ Approve' : '🔒 Job Incomplete'}
                      </button>
                    </div>
                  ) : tab === 'approved' ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsPaid(c.id, c.amount, c.users?.name || 'Unknown'); }}
                      disabled={markingPaid === c.id}
                      style={{
                        background: '#8B5CF6',
                        color: '#fff',
                        padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        opacity: markingPaid === c.id ? 0.6 : 1,
                        cursor: 'pointer',
                        border: 'none',
                      }}
                    >
                      {markingPaid === c.id ? 'Processing...' : '💵 Mark Paid'}
                    </button>
                  ) : (
                    <span style={{ color: '#10B981', fontSize: 13 }}>
                      {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {display.length === 0 && !loading && (
          <div style={{ padding: 60, textAlign: 'center', color: '#4A6580' }}>
            {tab === 'pending' ? '🎉 No pending commissions' : tab === 'approved' ? '🎉 No approved commissions awaiting payment' : 'No commission history yet'}
          </div>
        )}
      </div>
    </div>
  );
}
